import type { Protocol } from './schema';

/**
 * RI Emergency Medical Services Protocols
 * Source: RIDOH EMS Division
 *
 * Initial load covers high-frequency protocols for Providence FD.
 * Admin interface allows medical director to add/update/version protocols.
 */
export const RI_PROTOCOLS: Protocol[] = [
  // ===== CARDIAC =====
  {
    id: 'ri-cardiac-acs',
    name: 'Acute Coronary Syndrome (ACS)',
    category: 'cardiac',
    version: '2026.01',
    effectiveDate: '2026-04-01',
    source: 'RI Statewide EMS Protocols and Standing Orders v2026.01',
    alsLevel: 'ALS-1',
    pediatricOverlay: false,
    notes: null,
    triggers: [
      { type: 'chief_complaint', value: 'Chest Pain' },
      { type: 'dispatch_reason', value: 'Chest Pain' },
      { type: 'primary_impression', value: 'Acute Coronary Syndrome' },
    ],
    requirements: [
      { key: 'acs_12lead_acquired', label: '12-lead ECG acquired', type: 'boolean', required: true, cite: 'RI Protocol 3.02 — 12-lead required for all suspected ACS' },
      { key: 'acs_12lead_time', label: 'Time 12-lead acquired', type: 'datetime', required: true, cite: 'RI Protocol 3.02 — ≤10 min from patient contact' },
      { key: 'acs_12lead_interpretation', label: 'ECG interpretation', type: 'select', options: ['No STEMI', 'Anterior STEMI', 'Inferior STEMI', 'Lateral STEMI', 'Posterior STEMI', 'LBBB', 'Non-diagnostic'], required: true, cite: 'RI Protocol 3.02' },
      { key: 'acs_stemi_alert', label: 'STEMI alert activated (if STEMI)', type: 'boolean', required: false, cite: 'RI Protocol 3.02 — CODE STEMI notification for PCI facility' },
      { key: 'acs_ecg_transmitted', label: 'ECG transmitted to receiving facility', type: 'boolean', required: false, cite: 'RI Protocol 3.02' },
      { key: 'acs_12lead_attachment', label: '12-lead tracing attached', type: 'attachment', attachmentCategory: '12-lead-ecg', required: true, cite: 'RI Protocol 1.02 §10 — ECG tracings must be uploaded into the PCR' },
      { key: 'acs_destination_type', label: 'Destination type', type: 'select', options: ['PCI-capable', 'Closest ED (non-PCI)', 'Patient refusal'], required: true, cite: 'RI Protocol 3.02' },
    ],
    steps: [
      {
        order: 1, type: 'assessment',
        description: 'Obtain 12-lead EKG within 10 minutes of patient contact',
        plainLanguage: 'Get a 12-lead as fast as possible — this tells us if the heart is having an active heart attack.',
        billing: [{ type: 'HCPCS', code: '93005', description: '12-lead EKG interpretation' }],
        requiredCert: 'EMT-B', timeSensitive: true, contraindications: [],
        procedureType: '12-Lead Acquisition',
      },
      {
        order: 2, type: 'medication',
        description: 'Aspirin 324mg PO (chewed)',
        plainLanguage: 'Give the patient 4 baby aspirin to chew — this thins the blood and helps protect the heart.',
        medication: { drugName: 'Aspirin', adultDose: '324 mg', adultRoute: 'PO (chewed)', pediatricDose: null, pediatricRoute: null, maxDose: '324 mg', repeatInterval: null, maxRepeats: null },
        billing: [{ type: 'HCPCS', code: 'A0428', description: 'ALS non-emergency' }],
        requiredCert: 'EMT-B', timeSensitive: true,
        contraindications: ['Aspirin allergy', 'Active GI bleeding', 'Recent stroke'],
      },
      {
        order: 3, type: 'procedure',
        description: 'Establish IV access',
        plainLanguage: 'Start an IV line so we can give medications directly into the bloodstream if needed.',
        procedureType: 'IV Access',
        billing: [{ type: 'HCPCS', code: 'A0382', description: 'BLS routine supplies' }],
        requiredCert: 'AEMT', timeSensitive: false, contraindications: [],
      },
      {
        order: 4, type: 'decision',
        description: 'Assess for NTG administration',
        plainLanguage: null,
        decision: {
          question: 'Is SBP >= 100 mmHg and no erectile dysfunction medication in last 72 hours?',
          plainQuestion: 'Is the patient\'s blood pressure above 100 on top? And have they NOT taken Viagra, Cialis, or Levitra in the last 3 days?',
          yesAction: 'Administer NTG 0.4mg SL',
          noAction: 'Withhold NTG — document reason',
          vitalCheck: { field: 'bp_systolic', operator: '>=', value: 100, unit: 'mmHg' },
        },
        billing: [],
        requiredCert: 'AEMT', timeSensitive: false, contraindications: ['SBP < 100', 'ED medication use within 72hrs', 'Right-sided MI'],
      },
      {
        order: 5, type: 'medication',
        description: 'Nitroglycerin 0.4mg SL — may repeat x3 q5min if SBP >= 100',
        plainLanguage: 'Put a nitroglycerin tablet under the patient\'s tongue. This opens blood vessels and reduces chest pain. Recheck blood pressure before each dose.',
        medication: { drugName: 'Nitroglycerin', adultDose: '0.4 mg', adultRoute: 'SL (Sublingual)', pediatricDose: null, pediatricRoute: null, maxDose: '1.2 mg', repeatInterval: '5 minutes', maxRepeats: 3 },
        billing: [{ type: 'HCPCS', code: 'A0422', description: 'ALS defibrillation' }],
        requiredCert: 'AEMT', timeSensitive: false,
        contraindications: ['SBP < 100', 'ED medication use within 72hrs'],
      },
      {
        order: 6, type: 'medication',
        description: 'Fentanyl 25-50mcg IV/IN for pain management if NTG insufficient',
        plainLanguage: 'If the patient is still in significant pain after nitroglycerin, give pain medication through the IV or as a nasal spray.',
        medication: { drugName: 'Fentanyl', adultDose: '25-50 mcg', adultRoute: 'IV', pediatricDose: '1 mcg/kg', pediatricRoute: 'IN (Intranasal)', maxDose: '200 mcg', repeatInterval: '5 minutes', maxRepeats: 4 },
        billing: [{ type: 'ALS_LEVEL', code: 'ALS-1', description: 'ALS Level 1 assessment' }],
        requiredCert: 'Paramedic', timeSensitive: false,
        contraindications: ['Hypotension', 'Respiratory depression', 'Altered mental status'],
      },
      {
        order: 7, type: 'transport',
        description: 'Transport to closest PCI-capable facility — consider STEMI alert',
        plainLanguage: 'Take the patient to a hospital that can do heart catheterization. If the 12-lead shows a STEMI, call ahead so the cath lab team is ready when we arrive.',
        billing: [{ type: 'HCPCS', code: 'A0427', description: 'ALS emergency transport' }],
        requiredCert: null, timeSensitive: true, contraindications: [],
      },
    ],
  },

  // ===== CARDIAC ARREST =====
  {
    id: 'ri-cardiac-arrest',
    name: 'Cardiac Arrest',
    category: 'cardiac',
    version: '2026.01',
    effectiveDate: '2026-04-01',
    source: 'RI Statewide EMS Protocols and Standing Orders v2026.01',
    alsLevel: 'ALS-2',
    pediatricOverlay: true,
    notes: 'Follow current AHA guidelines for CPR and defibrillation.',
    triggers: [
      { type: 'dispatch_reason', value: 'Cardiac Arrest' },
      { type: 'primary_impression', value: 'Cardiac Arrest' },
    ],
    requirements: [
      { key: 'ca_witnessed', label: 'Arrest witnessed', type: 'select', options: ['Bystander witnessed', 'EMS witnessed', 'Unwitnessed'], required: true, cite: 'NEMSIS eArrest' },
      { key: 'ca_bystander_cpr', label: 'Bystander CPR before EMS arrival', type: 'boolean', required: true, cite: 'NEMSIS eArrest' },
      { key: 'ca_initial_rhythm', label: 'Initial cardiac rhythm', type: 'select', options: ['VF', 'Pulseless VT', 'PEA', 'Asystole', 'Unknown'], required: true, cite: 'NEMSIS eArrest' },
      { key: 'ca_advanced_airway_placed', label: 'Advanced airway placed', type: 'select', options: ['None', 'BIAD (i-Gel/King/LMA)', 'Endotracheal', 'Cricothyrotomy'], required: true, cite: 'RI Protocol 3.03 — airway documentation mandatory' },
      { key: 'ca_capnography_confirmed', label: 'Waveform capnography confirmed airway placement', type: 'boolean', required: true, cite: 'RI Protocol 5.01 — MANDATORY waveform capnography for all advanced airways' },
      { key: 'ca_capnography_attachment', label: 'EtCO2 waveform tracing uploaded', type: 'attachment', attachmentCategory: 'etco2-waveform', required: true, cite: 'RI Protocol 5.01 — capnography waveforms MUST be uploaded' },
      { key: 'ca_rosc_achieved', label: 'Return of spontaneous circulation (ROSC)', type: 'boolean', required: true, cite: 'NEMSIS eArrest' },
      { key: 'ca_receiving_physician', label: 'Name of accepting physician (for advanced airway patients)', type: 'text', required: true, cite: 'RI Protocol 5.01 — accepting physician name required in narrative' },
    ],
    steps: [
      {
        order: 1, type: 'procedure',
        description: 'Begin high-quality CPR — 100-120 compressions/min, 2" depth, full recoil',
        plainLanguage: 'Start chest compressions immediately. Push hard and fast in the center of the chest. Let the chest come all the way back up between pushes.',
        procedureType: 'CPR',
        billing: [{ type: 'HCPCS', code: 'A0427', description: 'ALS emergency transport' }],
        requiredCert: 'EMT-B', timeSensitive: true, contraindications: [],
      },
      {
        order: 2, type: 'procedure',
        description: 'Apply cardiac monitor/defibrillator — analyze rhythm',
        plainLanguage: 'Put the defibrillator pads on the patient\'s chest. The machine will tell us what rhythm the heart is in.',
        procedureType: 'Cardiac Monitoring',
        billing: [{ type: 'HCPCS', code: '93042', description: 'Rhythm strip interpretation' }],
        requiredCert: 'EMT-B', timeSensitive: true, contraindications: [],
      },
      {
        order: 3, type: 'decision',
        description: 'Rhythm analysis',
        plainLanguage: null,
        decision: {
          question: 'Is the rhythm shockable (VF/pulseless VT)?',
          plainQuestion: 'Does the monitor show V-Fib or V-Tach without a pulse? The machine will usually tell you if a shock is advised.',
          yesAction: 'Defibrillate at maximum energy, resume CPR immediately',
          noAction: 'Continue CPR — proceed to epinephrine',
        },
        billing: [],
        requiredCert: 'EMT-B', timeSensitive: true, contraindications: [],
      },
      {
        order: 4, type: 'procedure',
        description: 'Establish IV/IO access',
        plainLanguage: 'Start an IV or IO (a needle into the bone of the upper leg or arm) so we can give cardiac arrest drugs.',
        procedureType: 'IV Access',
        billing: [],
        requiredCert: 'AEMT', timeSensitive: true, contraindications: [],
      },
      {
        order: 5, type: 'medication',
        description: 'Epinephrine 1mg IV/IO q3-5min',
        plainLanguage: 'Give epinephrine through the IV. This medication stimulates the heart. We can repeat it every 3-5 minutes.',
        medication: { drugName: 'Epinephrine 1:10,000', adultDose: '1 mg', adultRoute: 'IV', pediatricDose: '0.01 mg/kg', pediatricRoute: 'IV/IO', maxDose: '1 mg per dose', repeatInterval: '3-5 minutes', maxRepeats: null },
        billing: [{ type: 'ALS_LEVEL', code: 'ALS-2', description: 'ALS Level 2 — cardiac arrest' }],
        requiredCert: 'AEMT', timeSensitive: true, contraindications: [],
      },
      {
        order: 6, type: 'medication',
        description: 'Amiodarone 300mg IV/IO for refractory VF/VT — then 150mg for second dose',
        plainLanguage: 'If the heart keeps going back into a shockable rhythm after multiple shocks, give amiodarone. This helps stabilize the heart\'s electrical activity.',
        medication: { drugName: 'Amiodarone', adultDose: '300 mg', adultRoute: 'IV', pediatricDose: '5 mg/kg', pediatricRoute: 'IV/IO', maxDose: '450 mg total', repeatInterval: null, maxRepeats: 1 },
        billing: [],
        requiredCert: 'Paramedic', timeSensitive: false,
        contraindications: ['Only for shockable rhythms (VF/pVT)'],
      },
      {
        order: 7, type: 'procedure',
        description: 'Advanced airway management — supraglottic or ETI',
        plainLanguage: 'Place a breathing tube to secure the patient\'s airway. This can be a King airway (goes in the throat) or an endotracheal tube (goes in the windpipe).',
        procedureType: 'Supraglottic Airway',
        billing: [{ type: 'HCPCS', code: '31500', description: 'Intubation, endotracheal' }],
        requiredCert: 'Paramedic', timeSensitive: false, contraindications: [],
      },
    ],
  },

  // ===== RESPIRATORY =====
  {
    id: 'ri-respiratory-distress',
    name: 'Respiratory Distress / Asthma / COPD',
    category: 'respiratory',
    version: '2026.01',
    effectiveDate: '2026-04-01',
    source: 'RI Statewide EMS Protocols and Standing Orders v2026.01',
    alsLevel: 'ALS-1',
    pediatricOverlay: true,
    notes: null,
    triggers: [
      { type: 'dispatch_reason', value: 'Breathing Problem' },
      { type: 'primary_impression', value: 'Asthma/COPD' },
      { type: 'primary_impression', value: 'Respiratory Distress' },
      { type: 'chief_complaint', value: 'Shortness of Breath' },
    ],
    requirements: [
      { key: 'resp_lung_sounds_right', label: 'Right lung sounds', type: 'select', options: ['Clear', 'Wheezes', 'Crackles', 'Rhonchi', 'Diminished', 'Absent'], required: true, cite: 'RI Protocol 2.08' },
      { key: 'resp_lung_sounds_left', label: 'Left lung sounds', type: 'select', options: ['Clear', 'Wheezes', 'Crackles', 'Rhonchi', 'Diminished', 'Absent'], required: true, cite: 'RI Protocol 2.08' },
      { key: 'resp_spo2_baseline', label: 'Baseline SpO2', type: 'number', min: 0, max: 100, unit: '%', required: true, cite: 'RI Protocol 2.08' },
      { key: 'resp_etco2_monitored', label: 'EtCO2 monitored', type: 'boolean', required: false, cite: 'RI Protocol 2.08' },
      { key: 'resp_response_to_treatment', label: 'Response to treatment', type: 'select', options: ['Improved', 'Unchanged', 'Worse', 'No treatment given'], required: true, cite: 'RI Protocol 2.08' },
    ],
    steps: [
      {
        order: 1, type: 'assessment',
        description: 'Assess airway, breathing, circulation. Auscultate bilateral lung sounds.',
        plainLanguage: 'Check if the patient can breathe and talk. Listen to both sides of the chest with a stethoscope for wheezing, crackles, or absent sounds.',
        billing: [],
        requiredCert: 'EMT-B', timeSensitive: false, contraindications: [],
      },
      {
        order: 2, type: 'monitor',
        description: 'Apply SpO2 and EtCO2 monitoring. Obtain baseline vitals.',
        plainLanguage: 'Put the pulse ox on the finger and the CO2 monitor on. Get a full set of vital signs.',
        billing: [{ type: 'HCPCS', code: '94760', description: 'Pulse oximetry' }],
        requiredCert: 'EMT-B', timeSensitive: false, contraindications: [],
      },
      {
        order: 3, type: 'medication',
        description: 'Albuterol 2.5mg nebulized — may repeat x3',
        plainLanguage: 'Give the patient a breathing treatment with albuterol. This opens up the airways. They breathe it in through a mask or mouthpiece.',
        medication: { drugName: 'Albuterol', adultDose: '2.5 mg', adultRoute: 'Nebulized', pediatricDose: '2.5 mg', pediatricRoute: 'Nebulized', maxDose: '7.5 mg', repeatInterval: 'Continuous if severe', maxRepeats: 3 },
        billing: [{ type: 'HCPCS', code: '94640', description: 'Nebulizer treatment' }],
        requiredCert: 'EMT-B', timeSensitive: true, contraindications: [],
      },
      {
        order: 4, type: 'medication',
        description: 'Ipratropium 0.5mg nebulized with first albuterol treatment',
        plainLanguage: 'Add ipratropium to the first nebulizer treatment. This gives extra help opening the airways.',
        medication: { drugName: 'Ipratropium', adultDose: '0.5 mg', adultRoute: 'Nebulized', pediatricDose: '0.25 mg', pediatricRoute: 'Nebulized', maxDose: '0.5 mg', repeatInterval: null, maxRepeats: 0 },
        billing: [],
        requiredCert: 'EMT-B', timeSensitive: false, contraindications: [],
      },
      {
        order: 5, type: 'decision',
        description: 'Assess response to bronchodilators',
        plainLanguage: null,
        decision: {
          question: 'Is SpO2 < 90% despite nebulizer treatment?',
          plainQuestion: 'Is the oxygen level still below 90% after the breathing treatment?',
          yesAction: 'Apply CPAP at 5-10 cmH2O',
          noAction: 'Continue monitoring, reassess',
          vitalCheck: { field: 'spo2', operator: '<', value: 90, unit: '%' },
        },
        billing: [],
        requiredCert: 'EMT-B', timeSensitive: true, contraindications: [],
      },
      {
        order: 6, type: 'procedure',
        description: 'CPAP at 5-10 cmH2O if poor response to nebulized medications',
        plainLanguage: 'If the breathing treatment isn\'t enough, put a CPAP mask on the patient. This pushes air into the lungs under pressure to help them breathe easier.',
        procedureType: 'CPAP',
        billing: [{ type: 'HCPCS', code: '94660', description: 'CPAP ventilation' }],
        requiredCert: 'EMT-B', timeSensitive: true,
        contraindications: ['Vomiting', 'Altered mental status', 'Pneumothorax', 'SBP < 90'],
      },
      {
        order: 7, type: 'medication',
        description: 'Epinephrine 0.3mg IM for severe bronchospasm / anaphylaxis component',
        plainLanguage: 'If the patient is in severe respiratory distress and not improving, give an epinephrine shot in the thigh muscle.',
        medication: { drugName: 'Epinephrine 1:1,000', adultDose: '0.3 mg', adultRoute: 'IM', pediatricDose: '0.01 mg/kg', pediatricRoute: 'IM', maxDose: '0.3 mg', repeatInterval: '5 minutes', maxRepeats: 2 },
        billing: [],
        requiredCert: 'EMT-B', timeSensitive: true,
        contraindications: [],
      },
    ],
  },

  // ===== NEUROLOGICAL — STROKE =====
  {
    id: 'ri-neuro-stroke',
    name: 'Stroke / CVA',
    category: 'neurological',
    version: '2026.01',
    effectiveDate: '2026-04-01',
    source: 'RI Statewide EMS Protocols and Standing Orders v2026.01',
    alsLevel: 'ALS-1',
    pediatricOverlay: false,
    notes: 'Time is brain. Document last known well time.',
    triggers: [
      { type: 'dispatch_reason', value: 'Stroke/CVA' },
      { type: 'primary_impression', value: 'CVA/Stroke' },
    ],
    requirements: [
      { key: 'stroke_last_known_well', label: 'Last Known Well (LKW) time', description: 'Date/time patient was last seen at baseline neurologic status. If symptoms noted on waking, document when patient went to sleep.', type: 'datetime', required: true, cite: 'RI Protocol 2.12 — time-critical for tPA eligibility' },
      { key: 'stroke_symptom_onset_on_waking', label: 'Symptoms noted on waking?', type: 'boolean', required: true, cite: 'RI Protocol 2.12' },
      { key: 'stroke_cincinnati_face', label: 'Cincinnati: Facial droop', type: 'select', options: ['Normal', 'Abnormal', 'Unable to assess'], required: true, cite: 'RI Protocol 2.12 — Cincinnati Stroke Scale' },
      { key: 'stroke_cincinnati_arm', label: 'Cincinnati: Arm drift', type: 'select', options: ['Normal', 'Abnormal', 'Unable to assess'], required: true, cite: 'RI Protocol 2.12' },
      { key: 'stroke_cincinnati_speech', label: 'Cincinnati: Speech', type: 'select', options: ['Normal', 'Abnormal', 'Unable to assess'], required: true, cite: 'RI Protocol 2.12' },
      { key: 'stroke_lams_score', label: 'LAMS score (0–5)', description: 'Facial droop (0/1) + Arm drift (0/1/2) + Grip strength (0/1/2)', type: 'number', min: 0, max: 5, required: true, cite: 'RI Protocol 2.12 — LAMS for LVO detection' },
      { key: 'stroke_bgl_checked', label: 'Blood glucose obtained', type: 'boolean', required: true, cite: 'RI Protocol 2.12 — rule out hypoglycemia mimicking stroke' },
      { key: 'stroke_destination', label: 'Destination type', type: 'select', options: ['Comprehensive Stroke Center', 'Primary Stroke Center', 'Acute Stroke Ready Hospital', 'Other'], required: true, cite: 'RI Protocol 2.12 — Table 2 Point of Entry' },
      { key: 'stroke_alert_activated', label: 'Stroke Alert activated with receiving facility', type: 'boolean', required: true, cite: 'RI Protocol 2.12' },
    ],
    steps: [
      {
        order: 1, type: 'assessment',
        description: 'Perform Cincinnati Stroke Scale or LAMS — document findings and score',
        plainLanguage: 'Check for stroke signs: facial droop (ask them to smile), arm drift (hold both arms out), speech difficulty (repeat a sentence). Document what you find.',
        billing: [],
        requiredCert: 'EMT-B', timeSensitive: true, contraindications: [],
      },
      {
        order: 2, type: 'assessment',
        description: 'Determine LAST KNOWN WELL time — document precisely',
        plainLanguage: 'Ask the patient or family: when was the last time the patient was completely normal? This time is critical for the hospital to decide treatment.',
        billing: [],
        requiredCert: 'EMT-B', timeSensitive: true, contraindications: [],
      },
      {
        order: 3, type: 'monitor',
        description: 'Obtain blood glucose — rule out hypoglycemia mimicking stroke',
        plainLanguage: 'Check the blood sugar. Low blood sugar can look exactly like a stroke. If it\'s low, we treat that first.',
        billing: [],
        requiredCert: 'EMT-B', timeSensitive: true, contraindications: [],
      },
      {
        order: 4, type: 'procedure',
        description: 'Establish IV access — do NOT delay transport for IV',
        plainLanguage: 'Start an IV if you can do it quickly. But do NOT stay on scene trying to get an IV — get the patient moving to the hospital first.',
        procedureType: 'IV Access',
        billing: [],
        requiredCert: 'AEMT', timeSensitive: false, contraindications: [],
      },
      {
        order: 5, type: 'transport',
        description: 'Transport to closest Stroke Center — activate Stroke Alert',
        plainLanguage: 'Take the patient to a hospital with a stroke team. Call ahead with a Stroke Alert so the CT scanner and neurologist are ready.',
        billing: [{ type: 'HCPCS', code: 'A0427', description: 'ALS emergency transport' }],
        requiredCert: null, timeSensitive: true, contraindications: [],
      },
    ],
  },

  // ===== MEDICAL — OVERDOSE =====
  {
    id: 'ri-medical-overdose',
    name: 'Overdose / Toxicology',
    category: 'medical',
    version: '2026.01',
    effectiveDate: '2026-04-01',
    source: 'RI Statewide EMS Protocols and Standing Orders v2026.01',
    alsLevel: 'ALS-1',
    pediatricOverlay: true,
    notes: 'RI has standing order for naloxone by all cert levels.',
    triggers: [
      { type: 'dispatch_reason', value: 'Overdose/Ingestion' },
      { type: 'primary_impression', value: 'Overdose/Poisoning' },
    ],
    requirements: [
      { key: 'od_substance_suspected', label: 'Suspected substance(s)', type: 'text', required: true, cite: 'RI Protocol 4.18' },
      { key: 'od_route_of_exposure', label: 'Route of exposure', type: 'select', options: ['Ingestion', 'Inhalation', 'Injection', 'Dermal', 'Unknown'], required: true, cite: 'RI Protocol 4.18' },
      { key: 'od_naloxone_response', label: 'Response to naloxone (if given)', type: 'select', options: ['Not given', 'Improved mental status', 'Improved respirations', 'Unchanged', 'Worse'], required: false, cite: 'RI Protocol 4.18' },
      { key: 'od_leave_behind_offered', label: 'Naloxone Leave-Behind kit offered', type: 'boolean', required: false, cite: 'RI Protocol 6.11' },
      { key: 'od_destination_facility', label: 'Destination type', type: 'select', options: ['Opioid Use Disorder Preferred Facility', 'Closest ED', 'Patient refused transport', 'BH Link'], required: true, cite: 'RI Protocol 4.18 — OUD Preferred Facilities' },
    ],
    steps: [
      {
        order: 1, type: 'assessment',
        description: 'Scene safety — assess for hazardous substances, needles, fentanyl exposure risk',
        plainLanguage: 'Be careful entering the scene. Look for needles, pills, drug paraphernalia. Watch out for powdered fentanyl — it can absorb through skin.',
        billing: [],
        requiredCert: 'EMT-B', timeSensitive: true, contraindications: [],
      },
      {
        order: 2, type: 'decision',
        description: 'Assess respiratory status',
        plainLanguage: null,
        decision: {
          question: 'Is respiratory rate < 10 or patient unresponsive with suspected opioid OD?',
          plainQuestion: 'Is the patient breathing very slowly (less than 10 times per minute) or not responding? Do you suspect opioid overdose (heroin, fentanyl, pills)?',
          yesAction: 'Administer Naloxone immediately',
          noAction: 'Monitor closely, supportive care',
          vitalCheck: { field: 'respiratory_rate', operator: '<', value: 10, unit: '/min' },
        },
        billing: [],
        requiredCert: 'EMT-B', timeSensitive: true, contraindications: [],
      },
      {
        order: 3, type: 'medication',
        description: 'Naloxone 2mg IN or 0.4mg IV/IM — may repeat q2-3min',
        plainLanguage: 'Spray naloxone into the patient\'s nose (2mg) or give it through IV/IM (0.4mg). This reverses opioid effects. The patient may wake up agitated — be ready.',
        medication: { drugName: 'Naloxone (Narcan)', adultDose: '2 mg IN or 0.4 mg IV', adultRoute: 'IN (Intranasal)', pediatricDose: '0.1 mg/kg', pediatricRoute: 'IN/IV', maxDose: '10 mg total', repeatInterval: '2-3 minutes', maxRepeats: 5 },
        billing: [{ type: 'HCPCS', code: 'A0427', description: 'ALS emergency transport' }],
        requiredCert: 'EMT-B', timeSensitive: true, contraindications: [],
      },
      {
        order: 4, type: 'procedure',
        description: 'Assist ventilations with BVM if respiratory rate < 8 or SpO2 < 90%',
        plainLanguage: 'If the patient isn\'t breathing well enough on their own, use the bag-valve mask to breathe for them until the naloxone works.',
        procedureType: 'BVM Ventilation',
        billing: [],
        requiredCert: 'EMT-B', timeSensitive: true, contraindications: [],
      },
      {
        order: 5, type: 'monitor',
        description: 'Monitor for re-sedation — naloxone wears off before most opioids',
        plainLanguage: 'Keep watching the patient carefully. Naloxone wears off in 30-90 minutes, but the opioid may last longer. They can stop breathing again.',
        billing: [],
        requiredCert: 'EMT-B', timeSensitive: false, contraindications: [],
      },
    ],
  },

  // ===== TRAUMA — HEMORRHAGE =====
  {
    id: 'ri-trauma-hemorrhage',
    name: 'Hemorrhage Control / Trauma',
    category: 'trauma',
    version: '2026.01',
    effectiveDate: '2026-04-01',
    source: 'RI Statewide EMS Protocols and Standing Orders v2026.01',
    alsLevel: 'BLS',
    pediatricOverlay: false,
    notes: null,
    triggers: [
      { type: 'dispatch_reason', value: 'Hemorrhage/Laceration' },
      { type: 'dispatch_reason', value: 'Stab/Gunshot' },
      { type: 'dispatch_reason', value: 'Traffic Accident' },
      { type: 'primary_impression', value: 'Hemorrhage' },
      { type: 'primary_impression', value: 'Trauma - Penetrating' },
      { type: 'primary_impression', value: 'Trauma - Blunt' },
    ],
    requirements: [
      { key: 'trauma_moi', label: 'Mechanism of injury', type: 'text', required: true, cite: 'RI Protocol 1.02 §6 — MOI required in narrative' },
      { key: 'trauma_tourniquet_time', label: 'Tourniquet application time (if placed)', type: 'datetime', required: false, cite: 'RI Protocol 4.04' },
      { key: 'trauma_hemorrhage_controlled', label: 'Hemorrhage controlled prior to transport', type: 'boolean', required: true, cite: 'RI Protocol 4.04' },
      { key: 'trauma_spinal_precautions', label: 'Spinal motion restriction indicated', type: 'select', options: ['Yes — applied', 'Yes — not applied (document reason)', 'No — not indicated'], required: true, cite: 'RI Protocol 4.03' },
      { key: 'trauma_destination', label: 'Destination type', type: 'select', options: ['Level 1 Trauma Center', 'Level 2 Trauma Center', 'Closest ED', 'Other'], required: true, cite: 'RI Protocol 4.01 — Table 2 Point of Entry' },
      { key: 'trauma_scene_time_min', label: 'Scene time (minutes)', description: 'For critical trauma, goal is ≤10 min on scene', type: 'number', min: 0, max: 120, unit: 'min', required: true, cite: 'RI Protocol 4.01' },
    ],
    steps: [
      {
        order: 1, type: 'assessment',
        description: 'Scene safety — BSI, mechanism of injury assessment',
        plainLanguage: 'Make sure the scene is safe. Put on gloves. Figure out what happened — how did the patient get hurt?',
        billing: [],
        requiredCert: 'EMT-B', timeSensitive: true, contraindications: [],
      },
      {
        order: 2, type: 'procedure',
        description: 'Control life-threatening hemorrhage — direct pressure, tourniquet for extremity',
        plainLanguage: 'Stop the bleeding. Press hard on the wound. If it\'s an arm or leg and won\'t stop bleeding, put a tourniquet on above the wound and tighten until bleeding stops.',
        procedureType: 'Hemorrhage Control - Tourniquet',
        billing: [{ type: 'HCPCS', code: 'A0382', description: 'BLS routine supplies' }],
        requiredCert: 'EMT-B', timeSensitive: true, contraindications: [],
      },
      {
        order: 3, type: 'procedure',
        description: 'Spinal motion restriction if mechanism warrants',
        plainLanguage: 'If the patient could have a neck or back injury (car crash, fall from height, dive accident), keep their spine still with a collar and board.',
        procedureType: 'Spinal Motion Restriction',
        billing: [],
        requiredCert: 'EMT-B', timeSensitive: false, contraindications: [],
      },
      {
        order: 4, type: 'procedure',
        description: 'Establish large-bore IV access x2 — NS wide open if signs of shock',
        plainLanguage: 'Start two large IVs. If the patient is showing signs of shock (fast heart rate, low blood pressure, pale skin), run the IV fluid wide open.',
        procedureType: 'IV Access',
        billing: [],
        requiredCert: 'AEMT', timeSensitive: true, contraindications: [],
      },
      {
        order: 5, type: 'medication',
        description: 'Fentanyl 25-50mcg IV for pain — hold if SBP < 90 or altered mental status',
        plainLanguage: 'If the patient is in severe pain and their blood pressure is above 90, give pain medication. Do NOT give pain medication if they\'re confused or have low blood pressure.',
        medication: { drugName: 'Fentanyl', adultDose: '25-50 mcg', adultRoute: 'IV', pediatricDose: '1 mcg/kg', pediatricRoute: 'IN (Intranasal)', maxDose: '200 mcg', repeatInterval: '5 minutes', maxRepeats: 4 },
        billing: [],
        requiredCert: 'Paramedic', timeSensitive: false,
        contraindications: ['SBP < 90', 'Altered mental status', 'Respiratory depression'],
      },
      {
        order: 6, type: 'transport',
        description: 'Transport to closest Trauma Center — minimize scene time < 10 minutes',
        plainLanguage: 'Get the patient to a trauma center fast. Try to be on scene less than 10 minutes for serious trauma — load and go.',
        billing: [{ type: 'HCPCS', code: 'A0427', description: 'ALS emergency transport' }],
        requiredCert: null, timeSensitive: true, contraindications: [],
      },
    ],
  },

  // ===== MEDICAL — DIABETIC EMERGENCY =====
  {
    id: 'ri-medical-diabetic',
    name: 'Diabetic Emergency',
    category: 'medical',
    version: '2026.01',
    effectiveDate: '2026-04-01',
    source: 'RI Statewide EMS Protocols and Standing Orders v2026.01',
    alsLevel: 'ALS-1',
    pediatricOverlay: true,
    notes: null,
    triggers: [
      { type: 'dispatch_reason', value: 'Diabetic Problem' },
      { type: 'primary_impression', value: 'Diabetic Emergency' },
      { type: 'dispatch_reason', value: 'Altered Mental Status' },
    ],
    requirements: [
      { key: 'diabetic_initial_bgl', label: 'Initial BGL', type: 'number', unit: 'mg/dL', required: true, cite: 'RI Protocol 2.10 — BGL must be obtained' },
      { key: 'diabetic_repeat_bgl', label: 'Repeat BGL after treatment', type: 'number', unit: 'mg/dL', required: false, cite: 'RI Protocol 2.10 — recheck BGL in 15 min after treatment' },
      { key: 'diabetic_mental_status_post', label: 'Mental status after treatment', type: 'select', options: ['Back to baseline', 'Improved but not baseline', 'Unchanged', 'Worse', 'Not treated'], required: true, cite: 'RI Protocol 2.10' },
    ],
    steps: [
      {
        order: 1, type: 'monitor',
        description: 'Obtain blood glucose level',
        plainLanguage: 'Check the blood sugar with a glucometer. This tells us right away if the problem is high or low sugar.',
        billing: [],
        requiredCert: 'EMT-B', timeSensitive: true, contraindications: [],
      },
      {
        order: 2, type: 'decision',
        description: 'Determine if hypoglycemia or hyperglycemia',
        plainLanguage: null,
        decision: {
          question: 'Is blood glucose < 60 mg/dL?',
          plainQuestion: 'Is the blood sugar below 60? If yes, this is low blood sugar and we need to fix it right now.',
          yesAction: 'Treat hypoglycemia — oral glucose if alert, D10 IV if altered',
          noAction: 'If BGL > 300 with AMS, consider DKA — IV NS, transport',
          vitalCheck: { field: 'blood_glucose', operator: '<', value: 60, unit: 'mg/dL' },
        },
        billing: [],
        requiredCert: 'EMT-B', timeSensitive: true, contraindications: [],
      },
      {
        order: 3, type: 'medication',
        description: 'If alert and swallowing: oral glucose 15g PO',
        plainLanguage: 'If the patient is awake and can swallow, give them oral glucose gel or juice with sugar.',
        medication: { drugName: 'Oral Glucose', adultDose: '15 g', adultRoute: 'PO (Oral)', pediatricDose: '15 g', pediatricRoute: 'PO', maxDose: '30 g', repeatInterval: '15 minutes', maxRepeats: 1 },
        billing: [],
        requiredCert: 'EMT-B', timeSensitive: true, contraindications: ['Cannot swallow', 'Unresponsive'],
      },
      {
        order: 4, type: 'medication',
        description: 'If altered/unable to swallow: Dextrose 10% 250mL IV',
        plainLanguage: 'If the patient can\'t swallow safely, give sugar water through the IV.',
        medication: { drugName: 'Dextrose 10%', adultDose: '250 mL', adultRoute: 'IV', pediatricDose: '5 mL/kg', pediatricRoute: 'IV', maxDose: '250 mL', repeatInterval: null, maxRepeats: 1 },
        billing: [{ type: 'HCPCS', code: 'A0427', description: 'ALS emergency transport' }],
        requiredCert: 'AEMT', timeSensitive: true, contraindications: [],
      },
      {
        order: 5, type: 'medication',
        description: 'If no IV access: Glucagon 1mg IM',
        plainLanguage: 'If you can\'t get an IV, give glucagon as a shot in the thigh. It takes 10-15 minutes to work.',
        medication: { drugName: 'Glucagon', adultDose: '1 mg', adultRoute: 'IM', pediatricDose: '0.5 mg', pediatricRoute: 'IM', maxDose: '1 mg', repeatInterval: null, maxRepeats: 0 },
        billing: [],
        requiredCert: 'EMT-B', timeSensitive: true, contraindications: [],
      },
      {
        order: 6, type: 'monitor',
        description: 'Recheck blood glucose in 15 minutes — repeat treatment if < 60',
        plainLanguage: 'Check the blood sugar again in 15 minutes. If it\'s still low, give another dose of sugar.',
        billing: [],
        requiredCert: 'EMT-B', timeSensitive: false, contraindications: [],
      },
    ],
  },

  // ===== NEUROLOGICAL — SEIZURE =====
  {
    id: 'ri-neuro-seizure',
    name: 'Seizure',
    category: 'neurological',
    version: '2026.01',
    effectiveDate: '2026-04-01',
    source: 'RI Statewide EMS Protocols and Standing Orders v2026.01',
    alsLevel: 'ALS-1',
    pediatricOverlay: true,
    notes: null,
    triggers: [
      { type: 'dispatch_reason', value: 'Convulsions/Seizure' },
      { type: 'primary_impression', value: 'Seizure' },
    ],
    requirements: [
      { key: 'seizure_duration_min', label: 'Seizure duration (minutes)', type: 'number', min: 0, unit: 'min', required: true, cite: 'RI Protocol 2.19 — >5 min = status epilepticus' },
      { key: 'seizure_type', label: 'Seizure type', type: 'select', options: ['Generalized tonic-clonic', 'Focal', 'Absence (petit mal)', 'Febrile (pediatric)', 'Status epilepticus', 'Unknown'], required: true, cite: 'RI Protocol 2.19' },
      { key: 'seizure_bgl', label: 'BGL obtained', type: 'boolean', required: true, cite: 'RI Protocol 2.19 — rule out hypoglycemia' },
      { key: 'seizure_postictal_duration', label: 'Postictal state duration (minutes, if resolved)', type: 'number', unit: 'min', required: false, cite: 'RI Protocol 2.19' },
    ],
    steps: [
      {
        order: 1, type: 'assessment',
        description: 'Protect patient from injury — do NOT restrain. Time the seizure.',
        plainLanguage: 'Keep the patient safe — move hard objects away. Do NOT put anything in their mouth. Start timing how long the seizure lasts.',
        billing: [],
        requiredCert: 'EMT-B', timeSensitive: true, contraindications: [],
      },
      {
        order: 2, type: 'monitor',
        description: 'Check blood glucose — hypoglycemia is a common treatable cause',
        plainLanguage: 'Check the blood sugar as soon as you can. Low blood sugar causes seizures and is easy to fix.',
        billing: [],
        requiredCert: 'EMT-B', timeSensitive: true, contraindications: [],
      },
      {
        order: 3, type: 'decision',
        description: 'Is seizure lasting > 5 minutes (status epilepticus)?',
        plainLanguage: null,
        decision: {
          question: 'Has the seizure been going for more than 5 minutes, or are seizures repeating without the patient waking up between them?',
          plainQuestion: 'Has the seizure lasted more than 5 minutes? Or is the patient having seizure after seizure without waking up in between?',
          yesAction: 'Administer benzodiazepine immediately',
          noAction: 'Monitor, supportive care, protect airway',
        },
        billing: [],
        requiredCert: 'EMT-B', timeSensitive: true, contraindications: [],
      },
      {
        order: 4, type: 'medication',
        description: 'Midazolam 5mg IN or 2mg IV for status epilepticus',
        plainLanguage: 'Spray midazolam into the patient\'s nose (5mg) or give it through IV (2mg). This medication stops the seizure.',
        medication: { drugName: 'Midazolam (Versed)', adultDose: '5 mg IN or 2 mg IV', adultRoute: 'IN (Intranasal)', pediatricDose: '0.1 mg/kg IN', pediatricRoute: 'IN (Intranasal)', maxDose: '10 mg', repeatInterval: '5 minutes', maxRepeats: 2 },
        billing: [{ type: 'ALS_LEVEL', code: 'ALS-1', description: 'ALS Level 1' }],
        requiredCert: 'Paramedic', timeSensitive: true,
        contraindications: ['Respiratory depression', 'Hypotension'],
      },
    ],
  },

  // ===== MEDICAL — ALLERGIC REACTION / ANAPHYLAXIS =====
  {
    id: 'ri-medical-anaphylaxis',
    name: 'Allergic Reaction / Anaphylaxis',
    category: 'medical',
    version: '2026.01',
    effectiveDate: '2026-04-01',
    source: 'RI Statewide EMS Protocols and Standing Orders v2026.01',
    alsLevel: 'ALS-1',
    pediatricOverlay: true,
    notes: null,
    triggers: [
      { type: 'dispatch_reason', value: 'Allergic Reaction' },
      { type: 'primary_impression', value: 'Allergic Reaction/Anaphylaxis' },
    ],
    requirements: [
      { key: 'anaphylaxis_severity', label: 'Severity', type: 'select', options: ['Mild', 'Moderate', 'Severe'], required: true, cite: 'RI Protocol 2.04' },
      { key: 'anaphylaxis_allergen', label: 'Suspected allergen/trigger', type: 'text', required: true, cite: 'RI Protocol 2.04' },
      { key: 'anaphylaxis_epi_given', label: 'Epinephrine given', type: 'boolean', required: true, cite: 'RI Protocol 2.04 — severe cases require IM epi' },
      { key: 'anaphylaxis_response_to_epi', label: 'Response to epinephrine', type: 'select', options: ['Improved', 'Unchanged', 'Worse', 'Not given'], required: false, cite: 'RI Protocol 2.04' },
    ],
    steps: [
      {
        order: 1, type: 'assessment',
        description: 'Identify allergen and remove exposure if possible',
        plainLanguage: 'Figure out what caused the allergic reaction (bee sting, food, medication) and stop the exposure if you can.',
        billing: [],
        requiredCert: 'EMT-B', timeSensitive: true, contraindications: [],
      },
      {
        order: 2, type: 'medication',
        description: 'Epinephrine 0.3mg IM (anterolateral thigh) for anaphylaxis',
        plainLanguage: 'If the patient has signs of anaphylaxis (trouble breathing, swelling, hives with low blood pressure), give epinephrine in the thigh. This is the most important medication.',
        medication: { drugName: 'Epinephrine 1:1,000', adultDose: '0.3 mg', adultRoute: 'IM', pediatricDose: '0.01 mg/kg', pediatricRoute: 'IM', maxDose: '0.3 mg per dose', repeatInterval: '5 minutes', maxRepeats: 2 },
        billing: [{ type: 'HCPCS', code: 'A0427', description: 'ALS emergency transport' }],
        requiredCert: 'EMT-B', timeSensitive: true, contraindications: [],
      },
      {
        order: 3, type: 'medication',
        description: 'Diphenhydramine 50mg IV/IM',
        plainLanguage: 'Give Benadryl through the IV or as a shot. This helps with hives and itching but is NOT a substitute for epinephrine in anaphylaxis.',
        medication: { drugName: 'Diphenhydramine (Benadryl)', adultDose: '50 mg', adultRoute: 'IV', pediatricDose: '1 mg/kg', pediatricRoute: 'IV/IM', maxDose: '50 mg', repeatInterval: null, maxRepeats: 0 },
        billing: [],
        requiredCert: 'AEMT', timeSensitive: false, contraindications: [],
      },
      {
        order: 4, type: 'medication',
        description: 'Albuterol 2.5mg nebulized if bronchospasm present',
        plainLanguage: 'If the patient is wheezing, give a breathing treatment just like we do for asthma.',
        medication: { drugName: 'Albuterol', adultDose: '2.5 mg', adultRoute: 'Nebulized', pediatricDose: '2.5 mg', pediatricRoute: 'Nebulized', maxDose: '7.5 mg', repeatInterval: null, maxRepeats: 2 },
        billing: [{ type: 'HCPCS', code: '94640', description: 'Nebulizer treatment' }],
        requiredCert: 'EMT-B', timeSensitive: false, contraindications: [],
      },
      {
        order: 5, type: 'procedure',
        description: 'IV access — NS bolus 20mL/kg if hypotensive',
        plainLanguage: 'Start an IV. If the blood pressure is low, give a big bag of saline fast to bring the pressure back up.',
        procedureType: 'IV Access',
        billing: [],
        requiredCert: 'AEMT', timeSensitive: true, contraindications: [],
      },
    ],
  },
];
